
import React from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/notifications/StatusBadges";
import { toast } from "sonner";
import { updateNotificationInDb } from "@/services/notificationUpdateService";
import { useQueryClient } from "@tanstack/react-query";
import { SupabaseNotification } from "@/types/notifications";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface StatusChangeDropdownProps {
  notification: SupabaseNotification;
  onClick: (e: React.MouseEvent) => void;
}

const StatusChangeDropdown = ({ notification, onClick }: StatusChangeDropdownProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const handleStatusChange = async (e: React.MouseEvent, newStatus: string) => {
    e.stopPropagation(); // Prevent notification click
    
    if (newStatus === notification.status) {
      return; // Status is already set to this value
    }
    
    const oldStatus = notification.status;
    
    try {
      // Update the notification status
      await updateNotificationInDb(
        notification.id,
        notification,
        { status: newStatus },
        user?.id,
        user?.name
      );
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      toast.success("Status bijgewerkt naar " + getStatusDisplayName(newStatus));
      
      // Send Telegram via edge function if status changed to actie_decoprof
      if (newStatus === 'actie_decoprof') {
        try {
          console.log("Triggering Decoprof Telegram from StatusChangeDropdown");
          const { data, error } = await supabase.functions.invoke('send-decoprof-telegram', {
            body: { notification_id: notification.id, old_status: oldStatus }
          });
          
          console.log("Decoprof Telegram result:", data);
          
          if (error) {
            toast.error("De status is gewijzigd, maar het Telegram bericht kon niet worden verzonden.");
          }
        } catch (telegramError) {
          console.error("Error sending Decoprof Telegram:", telegramError);
          toast.error("De status is gewijzigd, maar het Telegram bericht kon niet worden verzonden.");
        }
      }
    } catch (error) {
      console.error("Error updating notification status:", error);
      toast.error("Fout bij het bijwerken van de status");
    }
  };
  
  const getStatusDisplayName = (status: string) => {
    switch(status) {
      case "actie_luijten": return "Actie Luijten";
      case "actie_decoprof": return "Actie Decoprof";
      case "actie_winkel_ridderkerk": return "Actie winkel Ridderkerk";
      case "afwachting_klant": return "In afwachting van klant";
      case "afwachting_voorraad": return "In afwachting van nieuwe voorraad";
      case "afwachting_voorraad_ridderkerk": return "In afwachting van voorraad Ridderkerk";
      case "afwijkende_verzenddatum": return "Afwijkende verzenddatum";
      case "afgerond": return "Afgerond";
      default: return status;
    }
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={onClick}>
        <Button variant="ghost" size="sm" className="flex items-center gap-1 px-2 py-1">
          <StatusBadge status={notification.status} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="start" 
        className="w-60 max-h-[300px] overflow-y-auto" 
        side="bottom"
        sideOffset={5}
        avoidCollisions={true}
      >
        <DropdownMenuItem
          className={notification.status === "actie_luijten" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "actie_luijten")}
        >
          <StatusBadge status="actie_luijten" />
          <span className="ml-2">Actie Luijten</span>
          {notification.status === "actie_luijten" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "actie_decoprof" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "actie_decoprof")}
        >
          <StatusBadge status="actie_decoprof" />
          <span className="ml-2">Actie Decoprof</span>
          {notification.status === "actie_decoprof" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "actie_winkel_ridderkerk" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "actie_winkel_ridderkerk")}
        >
          <StatusBadge status="actie_winkel_ridderkerk" />
          <span className="ml-2">Actie winkel Ridderkerk</span>
          {notification.status === "actie_winkel_ridderkerk" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "afwachting_klant" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "afwachting_klant")}
        >
          <StatusBadge status="afwachting_klant" />
          <span className="ml-2">In afwachting van klant</span>
          {notification.status === "afwachting_klant" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "afwachting_voorraad" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "afwachting_voorraad")}
        >
          <StatusBadge status="afwachting_voorraad" />
          <span className="ml-2">In afwachting van voorraad</span>
          {notification.status === "afwachting_voorraad" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "afwachting_voorraad_ridderkerk" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "afwachting_voorraad_ridderkerk")}
        >
          <StatusBadge status="afwachting_voorraad_ridderkerk" />
          <span className="ml-2">In afwachting van voorraad Ridderkerk</span>
          {notification.status === "afwachting_voorraad_ridderkerk" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "afwijkende_verzenddatum" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "afwijkende_verzenddatum")}
        >
          <StatusBadge status="afwijkende_verzenddatum" />
          <span className="ml-2">Afwijkende verzenddatum</span>
          {notification.status === "afwijkende_verzenddatum" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          className={notification.status === "afgerond" ? "bg-slate-100" : ""}
          onClick={(e) => handleStatusChange(e, "afgerond")}
        >
          <StatusBadge status="afgerond" />
          <span className="ml-2">Afgerond</span>
          {notification.status === "afgerond" && <Check size={16} className="ml-2" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StatusChangeDropdown;
